import { USER_TOKEN } from 'utils/constants/local-storage-const';

interface CalculationConfig<T> {
  items: T[] | undefined;
  valueKey?: keyof T;
  calculation?: (item: T) => number;
}

export function calculateTotalUsdValue<T>({
  items,
  valueKey,
  calculation,
}: CalculationConfig<T>): number {
  if (!Array.isArray(items) || items.length === 0) return 0;
  const total = items?.reduce((acc, item) => {
    if (calculation) {
      const value = calculation(item);
      return isNaN(value) ? acc : acc + value;
    }
    if (valueKey) {
      const rawValue = item[valueKey];
      const value = Number(rawValue);
      return isNaN(value) ? acc : acc + value;
    }
    return acc;
  }, 0);
  return total;
}

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export function calculateAllUsdValues(userDetails: any): {
  totalVaultUsd: number;
  totalStakingUsd: number;
  totalClaimableUsd: number;
} {
  const totalVaultUsd = calculateTotalUsdValue({
    items: userDetails?.vaultShares,
    calculation: (item: { priceInUSD: number; balance: number }) =>
      Number(item?.priceInUSD) * Number(item?.balance),
  });

  const totalStakingUsd = calculateTotalUsdValue({
    items: userDetails?.staking,
    calculation: (item: {
      priceInUSD: number;
      totalStaked: number;
      tokenDecimals: number;
    }) =>
      (Number(item?.priceInUSD) * Number(item?.totalStaked)) /
      10 ** Number(item?.tokenDecimals),
  });

  const totalClaimableUsd = calculateTotalUsdValue({
    items: userDetails?.staking,
    calculation: (item: { claimable: number; tokenDecimals: number }) =>
      Number(item?.claimable) / 10 ** Number(item?.tokenDecimals),
  });

  return {
    totalVaultUsd,
    totalStakingUsd,
    totalClaimableUsd,
  };
}

export function formatDateToDDMonYYYY(
  dateInput: Date | string | number
): string {
  let date: Date;
  if (typeof dateInput === 'number') {
    date = new Date(dateInput * 1000);
  } else if (typeof dateInput === 'string') {
    const parsed = Number(dateInput);
    date = !isNaN(parsed) ? new Date(parsed * 1000) : new Date(dateInput);
  } else {
    date = dateInput;
  }
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
}

export function formatUsdParts(value: number): {
  whole: string;
  decimal: string;
} {
  const safeValue = isNaN(value) ? 0 : value;
  const [whole, decimal] = safeValue.toFixed(2).split('.');
  return { whole, decimal: `.${decimal}` };
}

export const getStoredTokens = (): Record<string, string> => {
  if (typeof localStorage === 'undefined') {
    return {};
  }
  const raw = localStorage.getItem(USER_TOKEN);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
    localStorage.removeItem(USER_TOKEN);
    return {};
  } catch {
    localStorage.removeItem(USER_TOKEN);
    return {};
  }
};

export const saveToken = (addr: string, token: string) => {
  const tokens = getStoredTokens();
  tokens[addr] = token;
  localStorage.setItem(USER_TOKEN, JSON.stringify(tokens));
};

export const removeToken = (addr: string) => {
  const tokens = getStoredTokens();
  if (addr in tokens) {
    delete tokens[addr];
    localStorage.setItem(USER_TOKEN, JSON.stringify(tokens));
  }
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true; // Consider malformed tokens as expired
  }
};

export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp;
  } catch {
    return null;
  }
};

export const getTokenTimeUntilExpiry = (token: string): number | null => {
  try {
    const expTime = getTokenExpirationTime(token);
    if (expTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      return expTime - currentTime;
    }
    return null;
  } catch {
    return null;
  }
};

export const getExpiredTokens = (): string[] => {
  const tokens = getStoredTokens();
  const expiredAddresses: string[] = [];

  for (const [address, token] of Object.entries(tokens)) {
    if (isTokenExpired(token)) {
      expiredAddresses.push(address);
    }
  }

  return expiredAddresses;
};

export const cleanupExpiredTokens = (): void => {
  const expiredAddresses = getExpiredTokens();
  expiredAddresses.forEach((address) => removeToken(address));
};

export const getValidTokens = (): Record<string, string> => {
  const tokens = getStoredTokens();
  const validTokens: Record<string, string> = {};

  for (const [address, token] of Object.entries(tokens)) {
    if (!isTokenExpired(token)) {
      validTokens[address] = token;
    }
  }

  return validTokens;
};

export const autoCleanupExpiredTokens = (): string[] => {
  const expiredAddresses = getExpiredTokens();

  if (expiredAddresses.length > 0) {
    cleanupExpiredTokens();
  }

  return expiredAddresses;
};

export const validateAndCleanupTokens = (): {
  valid: Record<string, string>;
  expired: string[];
} => {
  const expired = getExpiredTokens();
  const valid = getValidTokens();

  if (expired.length > 0) {
    cleanupExpiredTokens();
  }

  return { valid, expired };
};
